import React, { useMemo } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, FormFeedback, Button } from 'reactstrap';
import PropTypes from 'prop-types';
import { EMAIL_REGEX, WEBSITE_REGEX, validateIban, validatePhoneTr, validateTaxNumber } from '../../../_helper/regexHandler';
import { useModalCrudState } from '../../../_helper/hooks/useModalCrudState';

const InsuranceCompanyDataModal = ({ apiEndpoint, isOpen, toggle, mode, selectedCompany, refreshData }) => {
    const initialCompanyData = {
        id: '',
        name: '',
        address: '',
        phoneNumber: '',
        email: '',
        contactPerson: '',
        website: '',
        taxNumber: '',
        ibanNumber: '',
        isArbitrationMember: false,
    };

    const validationSchema = useMemo(() => ({
        name: {
            required: true,
            message: 'Şirket İsmi zorunludur.',
            minLength: 2,
            minLengthMessage: 'Şirket İsmi en az 2 karakter olmalıdır.',
        },
        address: {
            minLength: 5,
            minLengthMessage: 'Adres en az 5 karakter olmalıdır.',
        },
        phoneNumber: {
            validator: validatePhoneTr,
            validatorMessage: 'Telefon numarası 05 ile başlamalı ve 11 haneli olmalıdır.',
        },
        email: {
            regex: EMAIL_REGEX,
            regexMessage: 'Geçerli bir e-posta adresi giriniz.',
        },
        contactPerson: {
            minLength: 2,
            minLengthMessage: 'İletişim Kişisi en az 2 karakter olmalıdır.',
        },
        website: {
            regex: WEBSITE_REGEX,
            regexMessage: 'Geçerli bir web sitesi adresi giriniz (örn: https://site.com veya site.com).',
        },
        taxNumber: {
            validator: validateTaxNumber,
            validatorMessage: 'Vergi Numarası 10 haneli rakam olmalıdır.',
        },
        ibanNumber: {
            validator: validateIban,
            validatorMessage: 'Geçerli bir IBAN formatı giriniz (örn: TR12...).',
        },
    }), []);

    const {
        formData: companyData,
        errors,
        handleCancel,
        handleInputChange,
        handleSubmit,
        isSubmitting,
    } = useModalCrudState({
        isOpen,
        mode,
        selectedItem: selectedCompany,
        initialData: initialCompanyData,
        toggle,
        mapSelectedToData: ({ initialData, selectedItem }) => ({
            ...initialData,
            ...selectedItem,
            isArbitrationMember: selectedItem?.isArbitrationMember === true || selectedItem?.isArbitrationMember === 'yes' ? 'yes' : 'no',
        }),
        normalizeInput: true,
        validationSchema,
        submitConfig: {
            apiEndpoint,
            selectedItem: mode === 'modify' ? { id: selectedCompany?.id } : null,
            refreshData,
            componentName: 'Sigorta Şirketi',
            buildPayload: (data) => ({
                name: data.name || '',
                address: data.address || null,
                phoneNumber: data.phoneNumber || '',
                email: data.email || '',
                contactPerson: data.contactPerson || '',
                website: data.website || null,
                taxNumber: data.taxNumber || null,
                ibanNumber: data.ibanNumber || null,
                isArbitrationMember: data.isArbitrationMember === 'yes',
                ...(mode === 'modify' && selectedCompany?.id && { id: selectedCompany.id }),
            }),
        },
    });

    const getModalTitle = () => {
        return mode === 'add' ? 'Sigorta Şirketi Ekle' : 'Sigorta Şirketi Düzenle';
    };

    const getButtonLabel = () => {
        return mode === 'add' ? 'Ekle' : 'Güncelle';
    };

    return (
        <Modal isOpen={isOpen} toggle={handleCancel} backdrop="static">
            <ModalHeader toggle={handleCancel}>{getModalTitle()}</ModalHeader>
            <ModalBody>
                <Form>
                    <FormGroup>
                        <Label for="name">Şirket İsmi <span className="text-danger">*</span></Label>
                        <Input
                            type="text"
                            id="name"
                            name="name"
                            value={companyData.name || ''}
                            onChange={handleInputChange}
                            placeholder="Sigorta Şirketinin Tam Adı"
                            invalid={!!errors.name}
                        />
                        <FormFeedback>{errors.name}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="address">Adres</Label>
                        <Input
                            type="text"
                            id="address"
                            name="address"
                            value={companyData.address || ''}
                            onChange={handleInputChange}
                            placeholder="Şirketin Adresi (Opsiyonel)"
                            invalid={!!errors.address}
                        />
                        <FormFeedback>{errors.address}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="phoneNumber">Telefon Numarası</Label>
                        <Input
                            type="tel"
                            id="phoneNumber"
                            name="phoneNumber"
                            value={companyData.phoneNumber || ''}
                            onChange={handleInputChange}
                            placeholder="05XX XXX XX XX"
                            maxLength={11}
                            invalid={!!errors.phoneNumber}
                        />
                        <FormFeedback>{errors.phoneNumber}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="email">E-posta Adresi</Label>
                        <Input
                            type="email"
                            id="email"
                            name="email"
                            value={companyData.email || ''}
                            onChange={handleInputChange}
                            placeholder="ornek@sirket.com"
                            invalid={!!errors.email}
                        />
                        <FormFeedback>{errors.email}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="contactPerson">İletişim Kişisi</Label>
                        <Input
                            type="text"
                            id="contactPerson"
                            name="contactPerson"
                            value={companyData.contactPerson || ''}
                            onChange={handleInputChange}
                            placeholder="Yetkili Kişinin Adı Soyadı"
                            invalid={!!errors.contactPerson}
                        />
                        <FormFeedback>{errors.contactPerson}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="website">Web Sitesi</Label>
                        <Input
                            type="url"
                            id="website"
                            name="website"
                            value={companyData.website || ''}
                            onChange={handleInputChange}
                            placeholder="https://www.sirket.com (Opsiyonel)"
                            invalid={!!errors.website}
                        />
                        <FormFeedback>{errors.website}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="taxNumber">Vergi Numarası</Label>
                        <Input
                            type="text"
                            id="taxNumber"
                            name="taxNumber"
                            value={companyData.taxNumber || ''}
                            onChange={handleInputChange}
                            placeholder="10 Haneli Vergi Numarası (Opsiyonel)"
                            maxLength={10}
                            invalid={!!errors.taxNumber}
                        />
                        <FormFeedback>{errors.taxNumber}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="ibanNumber">IBAN Numarası</Label>
                        <Input
                            type="text"
                            id="ibanNumber"
                            name="ibanNumber"
                            value={companyData.ibanNumber || ''}
                            onChange={handleInputChange}
                            placeholder="TR00 0000 0000 0000 0000 0000 00"
                            invalid={!!errors.ibanNumber}
                        />
                        <FormFeedback>{errors.ibanNumber}</FormFeedback>
                    </FormGroup>
                    <FormGroup className="mb-3">
                        <Label for="isArbitrationMemberRadio" className="text-nowrap mb-0">
                            Tahkime Üye Mi?
                        </Label>
                        <div className="d-flex mt-2">
                            <div className="radio radio-success me-3 d-flex align-items-center">
                                <Input
                                    id="isArbitrationMemberTrue"
                                    type="radio"
                                    name="isArbitrationMember"
                                    value="yes"
                                    checked={companyData.isArbitrationMember === "yes"}
                                    onChange={handleInputChange}
                                />
                                <Label for="isArbitrationMemberTrue" className="ms-1 mb-0">
                                    Evet
                                </Label>
                            </div>
                            <div className="radio radio-danger d-flex align-items-center">
                                <Input
                                    id="isArbitrationMemberFalse"
                                    type="radio"
                                    name="isArbitrationMember"
                                    value="no"
                                    checked={companyData.isArbitrationMember === "no" || companyData.isArbitrationMember === false || companyData.isArbitrationMember === null}
                                    onChange={handleInputChange}
                                />
                                <Label for="isArbitrationMemberFalse" className="ms-1 mb-0">
                                    Hayır
                                </Label>
                            </div>
                        </div>
                    </FormGroup>
                </Form>
            </ModalBody>
            <ModalFooter>
                <Button color="outline-success" className="rounded-pill" onClick={handleSubmit} disabled={isSubmitting}>
                    {isSubmitting ? 'Kaydediliyor...' : getButtonLabel()}
                </Button>
                <Button color="outline-danger" onClick={handleCancel} className="ms-2 rounded-pill">
                    İptal
                </Button>
            </ModalFooter>
        </Modal>
    );
};

InsuranceCompanyDataModal.propTypes = {
    apiEndpoint: PropTypes.string.isRequired,
    isOpen: PropTypes.bool.isRequired,
    toggle: PropTypes.func.isRequired,
    mode: PropTypes.oneOf(['add', 'modify']).isRequired,
    selectedCompany: PropTypes.object,
    refreshData: PropTypes.func.isRequired,
};

export default InsuranceCompanyDataModal;
